/* ============================================================================
   Scrunchy auto-gift — "Buy 1 Starter Kit, get 1 Concentrate free."
   ----------------------------------------------------------------------------
   Pairs with the AUTOMATIC Buy X Get Y discount of the same name in Shopify
   Admin. Shopify prices the gift; this script only makes sure the gift line is
   physically in the cart whenever a Starter Kit is, and takes it back out when
   the kit leaves. No prices, dates or discount logic live here.

   Behaviour
   - Kit in cart, no gift line  -> add 1 Concentrate tagged with a hidden
     line-item property (_scrunchy_gift), so it is its own line and never
     merges with a Concentrate the customer bought on purpose.
   - After adding, re-read the cart. If Shopify did NOT zero the line (deal
     ended, limits hit, discount paused), remove it again and stop trying for
     this session. That is the date gate: the discount's own schedule rules.
   - Kit removed -> remove the gift line.
   - Customer removes the gift line themselves -> respect it for the session.
   - Cart drawer re-renders in place; the /cart page reloads once.
   ========================================================================== */
(function () {
  'use strict';

  var KIT_HANDLE = 'starter-kit';
  var GIFT_HANDLE = 'multi-surface-cleaner-concentrate';
  var GIFT_VARIANT_ID = 45897299198125;
  var GIFT_PROP = '_scrunchy_gift';
  var SS_DECLINED = 'scrunchy_gift_declined'; // deal not applying -> stop
  var SS_REMOVED = 'scrunchy_gift_removed';   // customer removed it -> stop

  var busy = false;
  var JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  function flag(key) {
    try { return sessionStorage.getItem(key) === '1'; } catch (e) { return false; }
  }
  function setFlag(key) {
    try { sessionStorage.setItem(key, '1'); } catch (e) { /* private mode: fine */ }
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function isGift(item) {
    return !!(item.properties && item.properties[GIFT_PROP]);
  }

  function kitQty(cart) {
    return cart.items.reduce(function (n, i) {
      return i.handle === KIT_HANDLE ? n + i.quantity : n;
    }, 0);
  }

  function hasFreeConcentrate(cart) {
    return cart.items.some(function (i) {
      return i.handle === GIFT_HANDLE && i.final_line_price === 0;
    });
  }

  function addGift() {
    var props = {};
    props[GIFT_PROP] = 'Free with Starter Kit';
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: props }] })
    }).then(function (r) { return r.json(); });
  }

  function removeLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(function (r) { return r.json(); });
  }

  /* Re-render Dawn's cart drawer + header bubble without a page reload. Opens
     the drawer only if it was already open (never pops it on page load). */
  function refreshUI() {
    // /cart page: Dawn renders <cart-items> there (the drawer uses
    // <cart-drawer-items>, and the drawer markup exists on every page, so
    // check the page first). One reload shows the right lines.
    if (document.querySelector('cart-items')) {
      window.location.reload();
      return Promise.resolve();
    }
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve();
    var wasOpen = drawer.classList.contains('active');
    return fetch('/?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var parse = function (html, sel) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var el = doc.querySelector(sel);
          return el ? el.innerHTML : null;
        };
        var drawerHtml = parse(sections['cart-drawer'], '#CartDrawer');
        var target = document.getElementById('CartDrawer');
        if (drawerHtml !== null && target) {
          target.innerHTML = drawerHtml;
          var inner = drawer.querySelector('.drawer__inner');
          if (inner) inner.classList.remove('is-empty');
          var overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
        }
        var bubbleHtml = parse(sections['cart-icon-bubble'], '.shopify-section');
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubbleHtml !== null && bubble) bubble.innerHTML = bubbleHtml;
        if (wasOpen && typeof drawer.open === 'function') drawer.open();
      })
      .catch(function () { /* cosmetic only */ });
  }

  function sync() {
    if (busy) return Promise.resolve();
    busy = true;
    return getCart().then(function (cart) {
      var kits = kitQty(cart);
      var giftLines = cart.items.filter(isGift);

      // 1. Kit present, nothing free yet -> add the gift (unless told not to).
      if (kits > 0 && giftLines.length === 0 && !hasFreeConcentrate(cart)) {
        if (flag(SS_DECLINED) || flag(SS_REMOVED)) return;
        return addGift().then(getCart).then(function (after) {
          var gift = after.items.filter(isGift)[0];
          if (!gift) return;
          if (gift.final_line_price > 0) {
            // Shopify did not make it free: deal inactive. Undo, stop trying.
            setFlag(SS_DECLINED);
            return removeLine(gift.key).then(refreshUI);
          }
          return refreshUI();
        });
      }

      // 2. Kit gone but gift still here -> take the gift back out.
      if (kits === 0 && giftLines.length > 0) {
        var chain = Promise.resolve();
        giftLines.forEach(function (g) {
          chain = chain.then(function () { return removeLine(g.key); });
        });
        return chain.then(refreshUI);
      }
    }).catch(function (e) {
      if (window.console) console.warn('[scrunchy-gift]', e);
    }).then(function () { busy = false; });
  }

  function onCartUpdate(evt) {
    // Customer deleted the gift line in the drawer/cart: don't fight them.
    if (evt && evt.source === 'cart-items' && String(evt.variantId) === String(GIFT_VARIANT_ID)) {
      var stillThere = evt.cartData && evt.cartData.items &&
        evt.cartData.items.some(isGift);
      if (!stillThere) { setFlag(SS_REMOVED); return; }
    }
    // Any new add clears a prior "declined" so the deal re-checks naturally.
    if (evt && evt.source === 'product-form') {
      try { sessionStorage.removeItem(SS_DECLINED); } catch (e) { /* noop */ }
    }
    sync();
  }

  function boot() {
    var ev = (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
    if (typeof window.subscribe === 'function') window.subscribe(ev, onCartUpdate);
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
